// =============================================================================
// INDUSTRY VOCAB — the shared, cross-user keyword/terminology lake.
//
// The second sibling under the shared-lake roof (beside public_events): the terms
// an ATS ranks on and recruiters search, per role family, researched ONCE and
// reused by everyone. Deliberately NO userId. It lets the resume + presence MIRROR
// the standard terms TRUTHFULLY (for ATS ranking + recruiter-search discoverability)
// where they are genuinely the candidate's, never stuffing, never claiming a term
// that is not theirs.
//
// Keyed by roleFamilyKey (a slug) so a family is researched + stored exactly once.
// lastVerifiedAt feeds the same assessRecency staleness banding as every other
// lake reference.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, jsonb, pgTable, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** A single searchable term and how it varies. */
export interface VocabTerm {
  /** The canonical keyword/skill/tool recruiters + ATS key on. */
  term: string;
  /** Variants/spellings a search or ATS may use. */
  aliases?: string[];
  /** Coarse bucket for weighting later: a hard skill, a tool, a method, a domain. */
  kind?: 'skill' | 'tool' | 'method' | 'domain' | 'title';
}

export interface VocabSource {
  url: string;
  title?: string;
  retrievedAt: string; // ISO, stamped at research time
}

export const industryVocab = pgTable(
  'industry_vocab',
  {
    id: id(),
    // Dedup key: slug of the role family, e.g. 'applied-ai-architect'. UNIQUE =
    // research-once is enforced here.
    roleFamilyKey: varchar('role_family_key', { length: 120 }).notNull().unique(),
    // Display name, e.g. 'Applied AI Architect'.
    roleFamily: varchar('role_family', { length: 160 }).notNull(),
    // Canonical titles for this family (recruiter-facing).
    titles: jsonb('titles').$type<string[]>().default(sql`'[]'::jsonb`),
    // The high-signal terms ATS + recruiter search key on.
    keywords: jsonb('keywords').$type<VocabTerm[]>().default(sql`'[]'::jsonb`),
    // Provenance: every term traces here. Empty = unverified seed.
    sources: jsonb('sources').$type<VocabSource[]>().default(sql`'[]'::jsonb`),
    // 'seed' | 'researched' | 'unverified' | 'stale'.
    verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
    lastVerifiedAt: timestamp('last_verified_at', { withTimezone: true }),
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('industry_vocab_key_idx').on(t.roleFamilyKey),
  }),
);
